import { Box, useTheme } from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts';
import { useResizeObserver } from '@mantine/hooks';
import { labelMarkClasses } from '@mui/x-charts/ChartsLabel';

import { bloxChartColors } from '../../../../themes/constants';
import { useParsePieChartData } from './useParsePieChartData';
import { useWidgetContext } from 'components/Dashboard/Widget';
import PieCenterLabel from './PieChartCenterLabel';
import LabelGradients from './LabelGradients';
import { CHART_HEIGHT } from 'components/Dashboard/shared/constants';

const PieChart = () => {
  const { widget, data: widgetData } = useWidgetContext();
  const theme = useTheme();
  const [ref, rect] = useResizeObserver();

  // Use resize observer to determine responsive breakpoints based on component width
  const containerWidth = rect.width || 0;
  const isMobile = containerWidth > 0 && containerWidth < theme.breakpoints.values.sm;
  const isTablet = containerWidth > 0 && containerWidth < theme.breakpoints.values.md;
  const isLightTheme = theme.palette.mode === 'light';

  // Parse chart data.
  const data = useParsePieChartData(widget, widgetData);

  return (
    <Box
      ref={ref}
      data-testid="pie-chart"
      sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    >
      <MuiPieChart
        colors={Object.values(bloxChartColors)}
        height={CHART_HEIGHT}
        hideLegend={isMobile}
        margin={{
          top: 0,
          left: isMobile ? 0 : 160,
          right: 0,
          bottom: 0,
        }}
        series={[
          {
            innerRadius: '80%',
            paddingAngle: 1,
            cornerRadius: 3,
            data: data?.data || [],
            // Hide the value in the tooltip since the value/percentages are in the label.
            valueFormatter: () => '',
            highlightScope: { fade: 'global', highlight: 'item' },
          },
        ]}
        slotProps={{
          legend: {
            direction: 'vertical',
            position: {
              vertical: isTablet ? 'top' : 'middle',
              horizontal: 'start',
            },
            sx: {
              p: 0,
              [`.${labelMarkClasses}`]: {
                fill: !isTablet
                  ? 'url(#legend-text-fade-gradient)'
                  : isLightTheme
                    ? theme.blox.colors.bluegray
                    : theme.blox.colors.offwhite, // Depends on <LabelGradients />.
                fontSize: 13,
                fontWeight: 500,
                overflow: 'hidden',
                width: isTablet ? 200 : 100,
              },
            },
          },
        }}
        sx={{
          '.MuiChartsLegend-series': {
            '.MuiChartsLegend-mark': {
              rx: 100,
              maxWidth: 20,
            },
          },
        }}
      >
        <LabelGradients />
        <PieCenterLabel isTopLabel>{data?.centerLabel?.value}</PieCenterLabel>
        <PieCenterLabel>{data?.centerLabel?.label ?? ''}</PieCenterLabel>
      </MuiPieChart>
    </Box>
  );
};

export default PieChart;
